import React from "react";
import { Button, Col, Container, Form, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Consonants, Phoneme, Vowels } from "./Phonemes";
import { BackNavRow } from "../../common/CommonStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDeleteLeft, faPlay } from "@fortawesome/pro-regular-svg-icons";
import styled from "styled-components";
import { COLOR_GRAY_100, COLOR_GRAY_4F, COLOR_GRAY_D3, COLOR_BLACK } from "../../common/colors";
import { ApiContext } from "../../common/context/ApiContext";
import {
  AnalyticsContext,
  ViewPronunciationEvent,
} from "../../common/context/AnalyticsContext";

// Created on first play rather than at import, so Chrome's autoplay policy
// doesn't start it suspended and warn on every page load.
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  if (audioContext.state === "suspended") {
    void audioContext.resume();
  }
  return audioContext;
};

const play = (buffer) => {
  const ctx = getAudioContext();
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start();
};

export const playAudio = (arrayBuffer: ArrayBuffer) =>
  getAudioContext().decodeAudioData(arrayBuffer, play);

const PhonemeButton = styled.button`
  background-color: ${COLOR_GRAY_100};
  border: 1px solid ${COLOR_GRAY_4F};
  border-radius: 0.375rem;
  cursor: "pointer";
  width: 100%;
  padding: 5px;
  margin-top: 5px;

  :hover {
    background-color: ${COLOR_GRAY_D3};
  }
`;

const Underlined = styled.span`
  border-bottom: 2px solid ${COLOR_BLACK};
`;

const PhonemeLabel = ({ p }: { p: Phoneme }) => {
  const emphasisIndex = p.example.indexOf(p.emphasis);
  if (emphasisIndex === -1) {
    return <span>{p.example}</span>;
  } else {
    return (
      <span>
        {p.example.slice(0, emphasisIndex)}
        <Underlined>{p.emphasis}</Underlined>
        {p.example.slice(emphasisIndex + p.emphasis.length)}
      </span>
    );
  }
};

const PhonemeKeyboard = ({
  onPhonemeClicked,
}: {
  onPhonemeClicked: (ipa: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Container>
      {[
        ["consonants", t("custom_pronunciation_consonants"), Consonants],
        ["vowels", t("custom_pronunciation_vowels"), Vowels],
      ].map(([key, label, phonemes]: [string, string, Phoneme[]]) => (
        <React.Fragment key={key}>
          <Row className="mt-4">
            <h4>{label}</h4>
          </Row>
          <Row>
            {phonemes.map((phoneme: Phoneme) => {
              return (
                <Col
                  xs={4}
                  md={3}
                  lg={2}
                  key={phoneme.emphasis + "-" + phoneme.example}
                >
                  <PhonemeButton
                    style={{ width: "100%" }}
                    onClick={() => onPhonemeClicked(phoneme.ipa)}
                  >
                    <b>{phoneme.ipa}</b> as in <PhonemeLabel p={phoneme} />
                  </PhonemeButton>
                </Col>
              );
            })}
          </Row>
        </React.Fragment>
      ))}
    </Container>
  );
};

export const PronunciationInput: React.FC<{
  initialPhonetics: string[];
  onPronunciationUpdated: (phonetics: string[]) => void;
}> = ({ initialPhonetics, onPronunciationUpdated }) => {
  const { t } = useTranslation();
  const [phonetics, setPhonetics] = React.useState(initialPhonetics);
  const deleteLastPhoneme = React.useCallback(() => {
    const newPhonetics = [...phonetics];
    newPhonetics.pop();
    setPhonetics(newPhonetics);
  }, [phonetics, setPhonetics]);
  const { previewPhonics } = React.useContext(ApiContext);
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewPronunciationEvent());
  }, [logEvent]);

  const hasPhonetics = phonetics.length > 0;
  const previewPhonic = React.useCallback(() => {
    if (phonetics.length > 0) {
      previewPhonics(phonetics.join("")).then(playAudio);
    }
  }, [phonetics, previewPhonics]);

  return (
    <>
      <Container className="mt-3 mb-5">
        <BackNavRow
          label={t("generic_nav_back")}
          callback={() => onPronunciationUpdated(phonetics)}
        >
          <h2>{t("custom_pronunciation_title")}</h2>
        </BackNavRow>
        <Row className="justify-content-center mt-3">
          <Col xs={12} md={10} lg={8} xl={6}>
            <Stack direction="horizontal">
              <Button
                className="me-2"
                variant="light"
                onClick={previewPhonic}
                disabled={!hasPhonetics}
              >
                <FontAwesomeIcon icon={faPlay} />
              </Button>
              <Form.Control
                type="text"
                aria-label="Pronunciation"
                readOnly
                value={phonetics.join("")}
              />
              <Button
                className="ms-2"
                variant="light"
                onClick={deleteLastPhoneme}
              >
                <FontAwesomeIcon icon={faDeleteLeft} />
              </Button>
            </Stack>
          </Col>
        </Row>
        <Row className="justify-content-center mt-3">
          <Col xs="auto">{t("custom_pronunciation_description")}</Col>
        </Row>
        <Row>
          <PhonemeKeyboard
            onPhonemeClicked={(phoneme: string) =>
              setPhonetics([...phonetics, phoneme])
            }
          ></PhonemeKeyboard>
        </Row>
      </Container>
    </>
  );
};
